import React from 'react';
import { List } from 'reactstrap';
import { httpClient } from '@s20/services/httpClient';
import CommonContext, { ApiRoutes, AppNavPaths } from '../Common';
import {
    AppPageForm, onFieldChange, onReactSelectChanged, toasty,
} from '../common/forms/FormElements';
import CustomCircularProgress from '../common/CustomCircularProgress';
import ValidatedSelect from '../common/forms/ValidatedSelect';
import { Block, BlockBorder } from '../common/Block';
import { SaveButton } from '../common/forms/SaveButton';
import { ExecutionHistory } from './common/ExecutionHistory';

const DataTypes = [
    { label: 'Feature Flags & Settings', value: 'feature-flag' },
    { label: 'Roles & Groups', value: 'role' },
    { label: 'Delegates', value: 'delegate' },
    { label: 'Equipment Types', value: 'equipment-type' },
    { label: 'Charge Types', value: 'charge-type' },
];

class CopyTenantDataViewModel {
    targetTenantId = null;
    sourceTenantId = null;
    dataTypes = [];
}

class CopyTenantData extends React.Component {
    static contextType = CommonContext;

    constructor(props) {
        super(props);
        this.formRef = React.createRef();
        this.state = {
            formValidated: false,
            loading: false,
            saving: false,
            data: new CopyTenantDataViewModel(),
            isValidated: false,
            tenants: [],
        };

        this.onChange = this.onChange.bind(this);
        this.onReactSelectChanged = onReactSelectChanged.bind(this);
    }

    componentDidMount() {
        return this.populateState();
    }

    populateState = async () => {
        const [tenants] = await Promise.all([
            httpClient.js(ApiRoutes.typeAheads.tenants()),
        ]);

        this.setState({
            tenants,
        });
    };

    onChange = onFieldChange;

    onSubmit = async (data) => {
        const { tenants } = this.state;

        this.setState({ saving: true });

        try {
            await httpClient.post(
                ApiRoutes.adminTool.copyTenantData(),
                data,
            );

            this.setState({
                data: new CopyTenantDataViewModel(),
                isValidated: false,
            });

            const targetTenant = tenants.find((t) => t.value === data.targetTenantId);
            toasty.success(`Tenant data has been copied to ${targetTenant.label}`);

            // Refresh the table after successful submission
            await this.props.refreshUsageHistory();
        } catch (err) {
            console.error(err);
            toasty.error(
                'Error: Unable to copy tenant data',
            );
            await this.props.refreshUsageHistory();
        } finally {
            this.setState({ saving: false });
        }
    };

    render() {
        const {
            loading,
            saving,
            isValidated,
            tenants,
        } = this.state;

        if (loading) {
            return (
                <div className="confirmationContainer pt-2 d-flex flex-row align-items-center justify-content-center">
                    <CustomCircularProgress
                        color="secondary"
                        variant="indeterminate"
                        label={
                            this.props.match.params.id
                                ? 'Getting Tool History...'
                                : 'Loading form...'
                        }
                    />
                </div>
            );
        }

        if (!(this.context ?? {}).user) return null;

        return (
            <AppPageForm
                backUrl={AppNavPaths.AdminTool}
                backLabel="Back to Admin Tools"
                formShown={this.context.formIsOpen}
                formId="adminToolList"
                formName="adminToolList"
                formHeading="Copy Tenant Data"
                formRef={this.formRef}
                formIsColumn
                saving={saving}
                isValidated={isValidated}
                onSubmit={(values) => this.onSubmit(values)}
                initialValues={{
                    dataTypes: DataTypes.map((x) => x.value),
                }}
                setIsValidated={(value) => {
                    this.setState({ isValidated: value });
                }}
            >
                {({ setFieldValue }) => (
                    <>
                        <Block padding="medium">
                            <div>
                                <ValidatedSelect
                                    id="selectedSourceTenant"
                                    name="sourceTenantId"
                                    label="Select a source tenant"
                                    required
                                    options={tenants}
                                    error="A source tenant selection is required"
                                />

                                <ValidatedSelect
                                    id="selectedTargetTenant"
                                    name="targetTenantId"
                                    label="Select a target tenant"
                                    required
                                    options={tenants}
                                    error="A target tenant selection is required"
                                />

                                <ValidatedSelect
                                    name="dataTypes"
                                    label="What type(s) of data do you want to copy?"
                                    required
                                    isMulti
                                    asPrimitive
                                    options={DataTypes}
                                    error="A data type selection is required"
                                    helperText="Note: Copying charge types automatically copies over relevant groups, roles, and equipment types. Delegates are copied with their tenant role memberships and dispatch assignments when matching records exist in the target tenant."
                                    onSelectAll={() => {
                                        setFieldValue('dataTypes', DataTypes);
                                    }}
                                />

                                <BlockBorder />

                                <div className="d-flex justify-content-center">
                                    <SaveButton
                                        label="Submit"
                                    />
                                </div>
                            </div>
                        </Block>

                        <ExecutionHistory usageHistory={this.props.usageHistory}>
                            {(record) => (
                                <List type="unstyled">
                                    {record.input.sourceTenantName && (
                                        <li>
                                            <strong>Source:</strong>
                                            {' '}
                                            {record.input.sourceTenantName}
                                        </li>
                                    )}

                                    {record.input.targetTenantName && (
                                        <li>
                                            <strong>Target:</strong>
                                            {' '}
                                            {record.input.targetTenantName}
                                        </li>
                                    )}

                                    {record.input.sourceTenantId && (
                                        <li>
                                            <strong>Source:</strong>
                                            {' '}
                                            {record.input.sourceTenantId}
                                        </li>
                                    )}

                                    {record.input.targetTenantId && (
                                        <li>
                                            <strong>Target:</strong>
                                            {' '}
                                            {record.input.targetTenantId}
                                        </li>
                                    )}

                                    {record.input.dataTypes && (
                                        <li>
                                            <strong>Types:</strong>
                                            {' '}

                                            {record.input.dataTypes
                                                .split(', ')
                                                .map((type) => DataTypes.find((dt) => dt.value === type)?.label)
                                                .filter(Boolean)
                                                .join(', ')}
                                        </li>
                                    )}
                                </List>
                            )}
                        </ExecutionHistory>
                    </>
                )}
            </AppPageForm>
        );
    }
}

export default CopyTenantData;
